import { Form, Head } from '@inertiajs/react'
import Heading from '@/components/heading'
import InputError from '@/components/input-error'
import MultiplePathsInput from '@/components/multiple-paths-input'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import AppLayout from '@/layouts/app-layout'
import { serversPath, appPath, appBackupConfigsPath, newAppBackupConfigPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'
import type { App } from '@/types'

type BackupConfigNewProps = {
  app: Pick<App, 'id' | 'name' | 'db_type' | 'db_name' | 'location'>
}

export default function BackupConfigNew({ app }: BackupConfigNewProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Servers', href: serversPath() },
    { title: app.name, href: appPath(app.id) },
    { title: 'New Backup Config', href: newAppBackupConfigPath(app.id) },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="New Backup Configuration" />

      <div className="p-6">
        <Heading
        title="New Backup Configuration"
        description={`Configure automated backups for ${app.name}`}
      />

      <Card className="mt-6 max-w-2xl">
        <CardHeader>
          <CardTitle>Backup Settings</CardTitle>
          <CardDescription>
            Define what to backup and where to store it
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form
            method="post"
            action={appBackupConfigsPath(app.id)}
            className="space-y-6"
          >
            {({ processing, errors }) => (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="backup_type">Backup Type *</Label>
                    <select
                      id="backup_type"
                      name="backup_config[backup_type]"
                      required
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="directory">Directory</option>
                      <option value="database">Database</option>
                    </select>
                    <InputError messages={errors['backup_config.backup_type']} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="frequency">Frequency *</Label>
                    <select
                      id="frequency"
                      name="backup_config[frequency]"
                      required
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    <InputError messages={errors['backup_config.frequency']} />
                  </div>
                </div>

                <MultiplePathsInput
                  label="Source Paths"
                  description="Required for directory backups. Add one or more paths to backup."
                  defaultPaths={app.location ? [app.location] : []}
                  name="backup_config[source_paths]"
                  placeholder="/var/www/myapp"
                />
                <InputError messages={errors['backup_config.source_paths']} />

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="destination_type">Destination *</Label>
                    <select
                      id="destination_type"
                      name="backup_config[destination_type]"
                      required
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="local">Local Storage</option>
                      <option value="s3">Amazon S3</option>
                    </select>
                    <InputError messages={errors['backup_config.destination_type']} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="schedule_time">Schedule Time</Label>
                    <Input
                      id="schedule_time"
                      name="backup_config[schedule_time]"
                      type="time"
                      defaultValue="04:00"
                    />
                    <InputError messages={errors['backup_config.schedule_time']} />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="destination_path">Destination Path *</Label>
                  <Input
                    id="destination_path"
                    name="backup_config[destination_path]"
                    type="text"
                    required
                    placeholder="/backups/myapp or s3-bucket/prefix"
                  />
                  <p className="text-xs text-muted-foreground">
                    Local directory path or S3 bucket/prefix
                  </p>
                  <InputError messages={errors['backup_config.destination_path']} />
                </div>

                <div className="border-t pt-4">
                  <h4 className="mb-4 font-medium">Retention Settings</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="retention_daily">Daily Backups</Label>
                      <Input
                        id="retention_daily"
                        name="backup_config[retention_daily]"
                        type="number"
                        min="1"
                        defaultValue="5"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="retention_weekly">Weekly Backups</Label>
                      <Input
                        id="retention_weekly"
                        name="backup_config[retention_weekly]"
                        type="number"
                        min="1"
                        defaultValue="3"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="retention_monthly">Monthly Backups</Label>
                      <Input
                        id="retention_monthly"
                        name="backup_config[retention_monthly]"
                        type="number"
                        min="1"
                        defaultValue="3"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="enabled"
                    name="backup_config[enabled]"
                    value="true"
                    defaultChecked
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="enabled" className="text-sm font-normal">
                    Enable this backup configuration
                  </Label>
                </div>

                <div className="flex justify-end gap-4">
                  <Button type="button" variant="outline" onClick={() => history.back()}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={processing}>
                    {processing && <Spinner />}
                    Create Configuration
                  </Button>
                </div>
              </>
            )}
          </Form>
        </CardContent>
      </Card>
      </div>
    </AppLayout>
  )
}
